import type { AppliedFilter } from "@/components/kit";

import { useEffect, useMemo, useState } from "react";

import {
  FilterMultiSelect,
  FilterSegment,
  FilterSelect,
  FiltersPopover,
  PlainTextField,
} from "@/components/kit";
import { useGetAllBrands } from "@/hooks/api/brand/useGetAllBrands";
import { useGetAllCategories } from "@/hooks/api/category/useGetAllCategories";
import { useGetAllCountries } from "@/hooks/api/country/useGetAllCountries";
import { useGetAllSuppliers } from "@/hooks/api/supplier";
import { ProductFilters } from "@/types";

type Draft = {
  categories: string[];
  subCategories: string[];
  brand: string | null;
  countries: string[];
  supplier: string | null;
  priceFrom: string;
  priceTo: string;
  link: "any" | "true" | "false";
  stock: string | null;
};

const fromFilters = (filters: ProductFilters): Draft => ({
  categories: filters.categories ?? [],
  subCategories: filters.subCategories ?? [],
  brand: filters.brands?.[0] ?? null,
  countries: filters.countries ?? [],
  supplier: filters.suppliers?.[0] ?? null,
  priceFrom: filters.priceFrom != null ? String(filters.priceFrom) : "",
  priceTo: filters.priceTo != null ? String(filters.priceTo) : "",
  link:
    filters.hasScrapedProduct === undefined
      ? "any"
      : filters.hasScrapedProduct
        ? "true"
        : "false",
  stock:
    filters.checkInStock === undefined ? null : String(filters.checkInStock),
});

const toNumber = (text: string) => {
  const value = Number(text);

  return text.trim() !== "" && Number.isFinite(value) ? value : undefined;
};

const NONE: Partial<ProductFilters> = {
  categories: [],
  subCategories: [],
  brands: [],
  countries: [],
  suppliers: [],
  priceFrom: undefined,
  priceTo: undefined,
  hasScrapedProduct: undefined,
};

export function countActiveFilters(filters: ProductFilters) {
  return [
    filters.categories?.length,
    filters.subCategories?.length,
    filters.brands?.length,
    filters.countries?.length,
    filters.suppliers?.length,
    filters.priceFrom,
    filters.priceTo,
    filters.hasScrapedProduct !== undefined,
  ].filter(Boolean).length;
}

/** The Filters popover for the product list, and the tags for what is applied. */
export function useProductFilterControls(
  filters: ProductFilters,
  setFilters: (filters: Partial<ProductFilters>) => void,
) {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => fromFilters(filters));
  const { data: categories } = useGetAllCategories();
  const { data: brands } = useGetAllBrands();
  const { data: countries } = useGetAllCountries();
  const { data: suppliers } = useGetAllSuppliers();

  // The popover starts from what is applied each time it opens.
  useEffect(() => {
    if (isOpen) setDraft(fromFilters(filters));
  }, [isOpen]);

  const patch = (next: Partial<Draft>) => setDraft((d) => ({ ...d, ...next }));

  const subCategoryOptions = useMemo(
    () =>
      (categories ?? [])
        .filter((c) => draft.categories.includes(c.id))
        .flatMap((c) => c.subCategories)
        .filter((s, i, all) => all.findIndex((o) => o.id === s.id) === i)
        .map((s) => ({ id: s.id, label: s.name })),
    [categories, draft.categories],
  );

  const apply = () => {
    setFilters({
      categories: draft.categories,
      subCategories: draft.subCategories.filter((id) =>
        subCategoryOptions.some((o) => o.id === id),
      ),
      brands: draft.brand ? [draft.brand] : [],
      countries: draft.countries,
      suppliers: draft.supplier ? [draft.supplier] : [],
      priceFrom: toNumber(draft.priceFrom),
      priceTo: toNumber(draft.priceTo),
      hasScrapedProduct: draft.link === "any" ? undefined : draft.link === "true",
    });
    setIsOpen(false);
  };

  const clearAll = () => {
    setFilters(NONE);
    setDraft(fromFilters({}));
    setIsOpen(false);
  };

  const nameOf = (list: { id: string; name: string }[] | undefined, id: string) =>
    list?.find((item) => item.id === id)?.name ?? id;

  const applied: AppliedFilter[] = [
    ...(filters.categories ?? []).map((id) => ({
      id: `categories:${id}`,
      label: "Category",
      value: nameOf(categories, id),
    })),
    ...(filters.subCategories ?? []).map((id) => ({
      id: `subCategories:${id}`,
      label: "Sub category",
      value:
        categories
          ?.flatMap((c) => c.subCategories)
          .find((s) => s.id === id)?.name ?? id,
    })),
    ...(filters.brands ?? []).map((id) => ({
      id: `brands:${id}`,
      label: "Brand",
      value: nameOf(brands, id),
    })),
    ...(filters.countries ?? []).map((id) => ({
      id: `countries:${id}`,
      label: "Country",
      value: nameOf(countries, id),
    })),
    ...(filters.suppliers ?? []).map((id) => ({
      id: `suppliers:${id}`,
      label: "Supplier",
      value: nameOf(suppliers, id),
    })),
    ...(filters.priceFrom != null
      ? [{ id: "priceFrom", label: "Min price", value: `AED ${filters.priceFrom}` }]
      : []),
    ...(filters.priceTo != null
      ? [{ id: "priceTo", label: "Max price", value: `AED ${filters.priceTo}` }]
      : []),
    ...(filters.hasScrapedProduct !== undefined
      ? [
          {
            id: "hasScrapedProduct",
            label: "Scraped link",
            value: filters.hasScrapedProduct ? "Linked" : "Not linked",
          },
        ]
      : []),
  ];

  const remove = (id: string) => {
    const [key, value] = id.split(":");

    if (value !== undefined) {
      const list = (filters[key as "categories"] ?? []).filter(
        (item) => item !== value,
      );

      setFilters({ [key]: list } as Partial<ProductFilters>);
    } else {
      setFilters({ [key]: undefined } as Partial<ProductFilters>);
    }
  };

  const popover = (
    <FiltersPopover
      activeCount={countActiveFilters(filters)}
      isOpen={isOpen}
      onApply={apply}
      onClearAll={clearAll}
      onOpenChange={setIsOpen}
    >
      <FilterMultiSelect
        label="Category"
        options={(categories ?? []).map((c) => ({ id: c.id, label: c.name }))}
        value={draft.categories}
        onChange={(categories) => patch({ categories })}
      />
      <FilterMultiSelect
        isDisabled={draft.categories.length === 0}
        label="Sub Category"
        options={subCategoryOptions}
        placeholder={draft.categories.length ? "Any" : "Choose a category first"}
        value={draft.subCategories}
        onChange={(subCategories) => patch({ subCategories })}
      />
      <FilterSelect
        label="Brand"
        options={(brands ?? []).map((b) => ({ id: b.id, label: b.name }))}
        value={draft.brand}
        onChange={(brand) => patch({ brand })}
      />
      <FilterMultiSelect
        label="Country"
        options={(countries ?? []).map((c) => ({ id: c.id, label: c.name }))}
        value={draft.countries}
        onChange={(countries) => patch({ countries })}
      />
      <FilterSelect
        label="Supplier"
        options={(suppliers ?? []).map((s) => ({ id: s.id, label: s.name }))}
        value={draft.supplier}
        onChange={(supplier) => patch({ supplier })}
      />
      <div className="grid grid-cols-2 gap-3 px-3">
        <PlainTextField
          label="Min Price (AED)"
          name="priceFrom"
          placeholder="Any"
          value={draft.priceFrom}
          onChange={(priceFrom) => patch({ priceFrom })}
        />
        <PlainTextField
          label="Max Price (AED)"
          name="priceTo"
          placeholder="Any"
          value={draft.priceTo}
          onChange={(priceTo) => patch({ priceTo })}
        />
      </div>
      <FilterSegment
        label="Scraped Product Link"
        options={[
          { id: "any", label: "Any" },
          { id: "true", label: "Has Scraped Link" },
          { id: "false", label: "No Scraped Link" },
        ]}
        value={draft.link}
        onChange={(link) => patch({ link: link as Draft["link"] })}
      />
    </FiltersPopover>
  );

  return { popover, applied, remove, clearAll };
}
